"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { MessageSquare, ThumbsUp } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/components/ui/star-rating";
import { FeedbackType } from "@/app/types/feedback";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface FeedbackPanelProps {
    locationElement?: string;
    bookId?: string;
    /**
     * When true, only render the icon in the trigger button instead of
     * the "Feedback" label. This is useful for compact placements like
     * the top navigation bar.
     */
    iconOnly?: boolean;
    /**
     * Optional analysis ID for feedback related to a specific analysis
     */
    analysisId?: string;
    /**
     * Optional analysis name for feedback related to a specific analysis
     */
    analysisName?: string;
    /**
     * When true, use a different button style and icon (ThumbsUp) for analysis feedback
     */
    isAnalysisFeedback?: boolean;
    /**
     * Optional tooltip text for the trigger button
     */
    tooltipText?: string;
}

export function FeedbackPanel({
    locationElement,
    bookId,
    iconOnly = false,
    analysisId,
    analysisName,
    isAnalysisFeedback = false,
    tooltipText,
}: FeedbackPanelProps) {
    const router = useRouter();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [rating, setRating] = useState(3);
    const [isIssue, setIsIssue] = useState(false);
    const MAX_CHARS = 1000;

    // Reset form when sheet opens or closes
    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);

        // Reset form when sheet opens
        if (open) {
            setFeedback("");
            setRating(3);
            setIsIssue(false);
        }
    };

    // Handle issue/suggestion toggle change
    const handleIssueChange = (value: string) => {
        const isIssueValue = value === "true";
        setIsIssue(isIssueValue);

        // Reset rating to 0 for suggestions, 3 for issues
        setRating(isIssueValue ? 3 : 0);
    };

    const handleSubmit = async () => {
        // For issues, require a rating
        if (isIssue && (!rating || rating < 1)) {
            toast({
                title: "Please provide a rating",
                description: "Please select a star rating for this issue.",
                variant: "destructive",
            });
            return;
        }

        if (!feedback.trim()) {
            toast({
                title: "Please provide feedback",
                description:
                    "Please enter some feedback text before submitting.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // Make sure rating is 0 for suggestions
            const finalRating = isIssue ? rating : 0;

            const response = await fetch("/api/feedback", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    feedback_content: feedback,
                    star_rating: finalRating,
                    feedback_type: FeedbackType.EXPLICIT,
                    location_page: window.location.pathname,
                    location_url: window.location.href,
                    location_element: locationElement || (isAnalysisFeedback ? "AnalysisReportTopLevelFeedbackButton" : undefined),
                    is_issue: isIssue,
                    metadata: {
                        ...(bookId ? { bookId } : {}),
                        ...(analysisId ? { analysisId } : {}),
                        ...(analysisName ? { analysisName } : {}),
                    },
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to submit feedback");
            }

            toast({
                title: "Feedback submitted",
                description: "Thank you for your feedback!",
            });

            // Reset form and close sheet
            setFeedback("");
            setRating(3);
            setIsIssue(false);
            setIsOpen(false);

            // Refresh the page to update any data
            router.refresh();
        } catch (error) {
            console.error("Error submitting feedback:", error);
            toast({
                title: "Error",
                description:
                    "There was an error submitting your feedback. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const triggerButton = isAnalysisFeedback ? (
        <Button
            variant="secondary"
            size="xs"
            className="flex items-center gap-1 transition-all hover:shadow-md hover:-translate-y-1"
        >
            <ThumbsUp className="h-3.5 w-3.5" />
            <span>Rate this {analysisName || "analysis"}</span>
        </Button>
    ) : (
        <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 hover:bg-accent hover:text-accent-foreground"
        >
            <MessageSquare className="h-4 w-4" />
            {!iconOnly && <span>Feedback</span>}
        </Button>
    );

    return (
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
            <TooltipProvider delayDuration={0}>
                <Tooltip>
                    <SheetTrigger asChild>
                        <TooltipTrigger asChild>
                            {triggerButton}
                        </TooltipTrigger>
                    </SheetTrigger>
                    {tooltipText && <TooltipContent>{tooltipText}</TooltipContent>}
                </Tooltip>
            </TooltipProvider>
            <SheetContent className="w-[400px] sm:w-[540px]">
                <SheetHeader>
                    <SheetTitle>
                        {isAnalysisFeedback ? `Rate this ${analysisName || "analysis"}` : "Share your feedback"}
                    </SheetTitle>
                    <SheetDescription>
                        {isAnalysisFeedback 
                            ? `How helpful was the report '${analysisName || "analysis"}'? Your feedback helps us improve.`
                            : "We'd love to hear your thoughts on how we can improve the Story Stream."}
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium">Feedback type</h3>
                        <RadioGroup
                            defaultValue="false"
                            value={isIssue ? "true" : "false"}
                            onValueChange={handleIssueChange}
                            className="flex flex-col space-y-1"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="false" id="suggestion" />
                                <Label
                                    htmlFor="suggestion"
                                    className="font-normal"
                                >
                                    Suggestion
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="true" id="issue" />
                                <Label htmlFor="issue" className="font-normal">
                                    Issue
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>

                    {/* Only show star rating for issues */}
                    {isIssue && (
                        <div className="space-y-2">
                            <h3 className="text-sm font-medium">
                                How would you rate your experience?
                            </h3>
                            <div className="flex flex-col space-y-2">
                                <StarRating
                                    rating={rating}
                                    onChange={setRating}
                                    size="lg"
                                    className="py-2"
                                />
                                {rating === 0 && (
                                    <p className="text-xs text-destructive">
                                        * Please select a rating
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <h3 className="text-sm font-medium">
                            What feedback do you have for us?
                        </h3>
                        <Textarea
                            value={feedback}
                            onChange={(e) =>
                                setFeedback(e.target.value.slice(0, MAX_CHARS))
                            }
                            placeholder={
                                isIssue
                                    ? "Describe the issue or bug you encountered..."
                                    : "Share your suggestions, ideas, or general feedback..."
                            }
                            className="min-h-[120px]"
                        />
                        <div className="flex justify-end">
                            <p
                                className={cn(
                                    "text-xs",
                                    feedback.length >= MAX_CHARS
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                )}
                            >
                                {MAX_CHARS - feedback.length} characters left
                            </p>
                        </div>
                    </div>

                    <Button
                        onClick={handleSubmit}
                        className="w-full"
                        variant="default"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Submitting..." : "Submit Feedback"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
